import { useId } from "react";
import type { Tone } from "@web-ui/types/tone";

/*
 * Meter: a capacity bar (devices used of the plan's allowance), as the Tixora overview draws it (owner
 * 2026-10-13): the label and the figure on one line, a thin track whose fill is the accent, or the
 * warning tone once full, or the tone given. Only the bar moves, at --motion-base (none under reduced
 * motion); the figure is data and snaps. The bar is a `meter` for assistive technology, named by the
 * label. Styles in styles/data-display.css .meter.
 */
type MeterProps = {
    className?: string;
    value: number;
    max: number;
    label: React.ReactNode;
    note?: React.ReactNode;
    tone?: Tone;
    /** The figure at the end of the label line; "value / max" by default. */
    figure?: React.ReactNode;
};

const Meter = ({ className, value, max, label, note, tone, figure }: MeterProps) => {
    const id = useId();
    const full = max > 0 && value >= max;
    const percent = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
    return (
        <div className={`meter ${className || ""}`} data-tone={tone ?? (full ? "warning" : "info")}>
            <div className="meter__head">
                <span className="meter__label" id={id}>{label}</span>
                <span className="meter__figure">{figure ?? `${value} / ${max}`}</span>
            </div>
            <div className="meter__track" role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.min(value, max)} aria-labelledby={id}>
                <div className="meter__fill" style={{ width: `${percent}%` }} />
            </div>
            {note ? <div className="meter__note">{note}</div> : null}
        </div>
    );
};

export default Meter;
